import { Capacitor } from "@capacitor/core";
import { cx } from "class-variance-authority";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { OrderDetailsData, OrderHistoryData } from "@/services/CatalogTypes";

import { AdonisIdentity } from "../adonis.types";
import { AdonisChatTextAreaContainer } from "../AdonisChatComponent/AdonisChatTextAreaContainer";
import { useToolStatus } from "../AdonisChatComponent/AdonistToolCalResponseProvider";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import { MessageList } from "../components/MessageList";
import { renderToolStatusLine } from "../components/renderToolStatusLine";
import AdonisToolRenderer from "../components/tools/AdonisToolRenderer";
import { useCatalogService } from "../hooks/useCatalogService";
import { AdonisMarkdownService } from "../services/AdonisMarkdownService";
import { coerceIds } from "../tools/args";

import { BasicConversationEngine } from "./engine";
import { EngineMidStreamTypingIndicator } from "./EngineMidStreamTypingIndicator";
import { ConversationProvider } from "./provider";
import { AdonisHttpProvider } from "./providers/AdonisHttpProvider";
import { ComposableProvider } from "./providers/ComposableProvider";
import { SimpleToolRegistry } from "./tools/registry";
import { ConversationEngine } from "./types";
import {
  useConversationEngine,
  useConversationEngineStateOnly,
} from "./useConversationEngine";

export type EngineHttpChatHandle = {
  getEngine: () => ConversationEngine;
};

export const EngineHttpChat = forwardRef<
  EngineHttpChatHandle,
  {
    sessionId: string;
    providerFactory?: (sessionId: string) => ConversationProvider;
    catalogService?: ReturnType<typeof useCatalogService>;
    keyboardIsOpen?: boolean;
    keyboardHeight?: number;
    placeholder?: string;
    isWarmupComplete?: boolean;
    isWarmingUp?: boolean;
    emptyState?: React.ReactNode;
  }
>(function EngineHttpChat(
  {
    sessionId,
    providerFactory,
    catalogService: injectedCatalogService,
    keyboardIsOpen = false,
    keyboardHeight = 0,
    placeholder = "",
    isWarmupComplete = true,
    isWarmingUp = false,
    emptyState,
  },
  ref
) {
  const defaultCatalogService = useCatalogService();
  const catalogService = injectedCatalogService ?? defaultCatalogService;
  const toolStatus = useToolStatus();
  const engine = useMemo(() => {
    // Allow custom provider (e.g., direct Anthropic) with AdonisHttpProvider as default
    const provider: ConversationProvider = providerFactory
      ? providerFactory(sessionId)
      : new AdonisHttpProvider(sessionId);

    // Build a simple registry and wire concrete executors.
    const registry = new SimpleToolRegistry();

    // Catalog-backed executors
    registry.register("list_memberships", async () => {
      const catalogMembershipItems =
        await catalogService.getCatalogMembershipItems();
      const membershipItemIds = catalogMembershipItems.map(
        (membershipItem) => membershipItem.id
      );
      const essentialMembershipDetails = await Promise.all(
        membershipItemIds.map((membershipId) =>
          catalogService.fetchEssentialMembershipDetails(membershipId)
        )
      );
      return essentialMembershipDetails
        .map((essentialMembershipDetail) =>
          AdonisMarkdownService.formatMembershipDetailsAsMarkdown(
            essentialMembershipDetail
          )
        )
        .join("\n\n---\n\n");
    });

    registry.register("get_package_details", async (rawPackageArgs) => {
      const packageIds = coerceIds(rawPackageArgs);
      if (packageIds.length === 0)
        return "Error: Missing required argument: id or ids";
      try {
        const essentialPackageDetails = await Promise.all(
          packageIds.map((packageId) =>
            catalogService.fetchEssentialPackageDetails(packageId)
          )
        );
        return essentialPackageDetails
          .map((essentialPackageDetail) =>
            AdonisMarkdownService.formatPackageDetailsAsMarkdown(
              essentialPackageDetail
            )
          )
          .join("\n\n---\n\n");
      } catch (error) {
        return `Error: Unable to fetch package details. ${
          error instanceof Error ? error.message : "Please try again."
        }`;
      }
    });

    registry.register("get_membership_details", async (rawMembershipArgs) => {
      const membershipIds = coerceIds(rawMembershipArgs);
      if (membershipIds.length === 0)
        return "Error: Missing required argument: id or ids.";
      try {
        const essentialMembershipDetails = await Promise.all(
          membershipIds.map((membershipId) =>
            catalogService.fetchEssentialMembershipDetails(membershipId)
          )
        );
        return essentialMembershipDetails
          .map((essentialMembershipDetail) =>
            AdonisMarkdownService.formatMembershipDetailsAsMarkdown(
              essentialMembershipDetail
            )
          )
          .join("\n\n---\n\n");
      } catch (error) {
        return `Error: Unable to fetch membership details. ${
          error instanceof Error ? error.message : "Please try again."
        }`;
      }
    });

    registry.register("list_available_discounts", async () => {
      try {
        const availableDiscountsData =
          await catalogService.executeListAvailableDiscounts();
        return AdonisMarkdownService.formatAvailableDiscountsAsMarkdown(
          availableDiscountsData.shopDiscounts,
          availableDiscountsData.promotions,
          availableDiscountsData.listings,
          availableDiscountsData.memberships
        );
      } catch (error) {
        return `Error: Unable to fetch available discounts. ${
          error instanceof Error ? error.message : "Please try again"
        }`;
      }
    });

    registry.register("get_order_history", async (rawOrderHistoryArgs) => {
      const orderHistoryArgs = rawOrderHistoryArgs as {
        detailed?: boolean;
        id?: string;
        ids?: string[];
        forContext?: boolean;
      };
      const orderIds = coerceIds(orderHistoryArgs);
      try {
        const orderHistoryResult = await catalogService.executeGetOrderHistory({
          detailed: orderHistoryArgs.detailed,
          orderIds: orderIds.length > 0 ? orderIds : undefined,
          forContext: orderHistoryArgs.forContext,
        });
        switch (orderHistoryResult.type) {
          case "single":
            return AdonisMarkdownService.formatOrderDetailsAsMarkdown(
              orderHistoryResult.data as OrderDetailsData
            );
          case "multiple":
            return AdonisMarkdownService.formatMultipleOrderDetailsAsMarkdown(
              orderHistoryResult.data as OrderDetailsData[]
            );
          case "summary":
            return AdonisMarkdownService.formatOrderHistorySummaryAsMarkdown(
              orderHistoryResult.data as OrderHistoryData
            );
          case "detailed":
            return AdonisMarkdownService.formatDetailedOrderHistoryAsMarkdown(
              orderHistoryResult.data as OrderHistoryData,
              orderHistoryResult.details ?? []
            );
          case "behavior":
            return AdonisMarkdownService.formatOrderHistoryForBehaviorAnalysis(
              orderHistoryResult.data as OrderHistoryData,
              orderHistoryResult.details ?? []
            );
          default:
            return "Unknown order history format";
        }
      } catch (error) {
        return `Error: Unable to fetch order history. ${
          error instanceof Error ? error.message : "Please try again."
        }`;
      }
    });

    // Generative UI stubs
    registry.register("render_product_recommendations", async () => {
      return JSON.stringify({ success: true });
    });
    registry.register("render_cta_buttons", async () => {
      return JSON.stringify({ success: true });
    });

    const providerComposable = new ComposableProvider(
      async (providerParams) => {
        if (providerParams.kind === "user") {
          return provider.createUserStream(
            providerParams.userText,
            providerParams.history
          );
        }
        return provider.continueWithToolResults(
          providerParams.toolResultContent,
          providerParams.history
        );
      },
      registry,
      { sessionId }
    );

    return new BasicConversationEngine({
      provider: providerComposable,
      enableAnimations: true,
      typing: {
        charsPerSecond: 90,
        variance: 0.15,
        punctuationPauseMs: 120,
        minFrameMs: 16,
        accelerateWhenBacklogged: true,
        maxBacklogCharsForAcceleration: 400,
      },
    });
  }, [sessionId, catalogService, providerFactory]);

  // Expose imperative engine handle
  useImperativeHandle(ref, () => ({ getEngine: () => engine }), [engine]);

  useEffect(() => {
    const onToolCallStart = ({ id }: { id: string }) =>
      toolStatus.markToolLoading(id);
    engine.on("toolCallStart", onToolCallStart);
    return () => {
      engine.off("toolCallStart", onToolCallStart);
    };
  }, [engine, toolStatus]);

  useEffect(() => {
    const onToolExecuted = (executedEvent: {
      id: string;
      name: string;
      status: "success" | "failed";
      result?: string;
      error?: string;
    }) => {
      if (executedEvent.status === "success") {
        toolStatus.markToolSuccess(executedEvent.id, executedEvent.result);
      } else {
        toolStatus.markToolFailure(
          executedEvent.id,
          new Error(executedEvent.error || "Unknown error")
        );
      }
    };
    engine.on("toolExecuted", onToolExecuted);
    return () => {
      engine.off("toolExecuted", onToolExecuted);
    };
  }, [engine, toolStatus]);

  const { state } = useConversationEngineStateOnly(engine);
  const { visibleMessages } = useConversationEngine(engine);

  // Internal scroll container management
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !autoScrollEnabled) {
      return;
    }

    el.scrollTop = el.scrollHeight;
  }, [autoScrollEnabled]);

  const { setHasMessages } = useAdonisChat();
  // Auto-scroll while streaming/new messages if user is near bottom
  useEffect(() => {
    const onMessagesUpdated = () => {
      scrollToBottom();
      setHasMessages(true);
    };
    engine.on("messagesUpdated", onMessagesUpdated);
    return () => {
      engine.off("messagesUpdated", onMessagesUpdated);
    };
  }, [engine, setHasMessages, scrollToBottom]);

  // Track user scrolling to disable auto-scroll
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const thresholdPx = 64;
    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - (el.scrollTop + el.clientHeight);
      const nearBottom = distanceFromBottom <= thresholdPx;
      setAutoScrollEnabled(nearBottom);
    };
    el.addEventListener("scroll", onScroll, {
      passive: true,
    });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [scrollContainerRef]);

  // After initial/historical load ensure bottom alignment
  useEffect(() => {
    if (visibleMessages.length > 0) {
      scrollToBottom();
    }
  }, [visibleMessages, scrollToBottom]);

  // When native keyboard opens, nudge to bottom after slight delay
  useEffect(() => {
    if (keyboardIsOpen) {
      const id = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(id);
    }
  }, [keyboardIsOpen, scrollToBottom]);

  const handleSend = async (inputText?: string) => {
    const trimmedInputText = inputText?.trim();
    if (!trimmedInputText || state.isBusy) return;

    setAutoScrollEnabled(true);

    await engine.sendUserMessage(trimmedInputText);
  };

  return (
    <div
      className={cx(
        "relative flex h-full flex-col rounded-lg bg-gradient-to-b from-white to-[#ECDAC3] shadow-md"
      )}
    >
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden py-4"
        style={{
          paddingBottom: keyboardIsOpen
            ? Capacitor.isNativePlatform()
              ? `${keyboardHeight + 60}px`
              : "120px"
            : "0px",
          maxWidth: "100%",
          contain: "layout style",
        }}
      >
        {visibleMessages.length > 0 ? (
          <MessageListWrapper visibleMessages={visibleMessages} />
        ) : (
          emptyState ?? null
        )}
        <EngineMidStreamTypingIndicator state={state} />
      </div>
      <AdonisChatTextAreaContainer
        handleSendRecommendation={handleSend}
        keyboardIsOpen={keyboardIsOpen}
        keyboardHeight={keyboardHeight}
        placeholder={placeholder}
        isWarmupComplete={isWarmupComplete}
        isWarmingUp={isWarmingUp}
        isStreaming={state.isBusy}
      />
    </div>
  );
});

const MessageListWrapper = ({
  visibleMessages,
}: {
  visibleMessages: ReturnType<typeof useConversationEngine>["visibleMessages"];
}) => {
  const toolStatusRenderer = useCallback(
    ({
      toolName,
      toolId,
      status,
      persona,
      input,
    }: {
      toolName: string;
      toolId: string;
      status: "pending" | "success" | "failed";
      persona: AdonisIdentity;
      input?: Record<string, unknown> | undefined;
    }) => {
      // Prefer specialized renderer for known generative UI tools
      if (
        toolName === "render_product_recommendations" ||
        toolName === "render_cta_buttons" ||
        toolName === "render_cta_button"
      ) {
        return (
          <AdonisToolRenderer
            toolName={toolName}
            toolId={toolId}
            status={status}
            persona={persona}
            input={input}
            animateText={true}
          />
        );
      }
      return renderToolStatusLine({
        toolName,
        status,
        personaName: persona?.name,
        enableAnimation: true,
      });
    },
    []
  );
  return (
    <div className="px-4">
      <MessageList
        messages={visibleMessages}
        disableAnimations={false}
        toolStatusRenderer={toolStatusRenderer}
      />
    </div>
  );
};
